import { useRef, useState } from "react";
import { api, errorMessage } from "../api/client";
import type { Product } from "../api/types";

/** Longest side of the image we send. Size buttons stay readable well below a phone's full resolution. */
const MAX_SIDE = 1600;

/** Shrinks a picked image to a JPEG data URL so a phone screenshot uploads quickly. */
async function shrink(file: File): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, fail) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => fail(new Error("That file isn't an image we can open."));
      i.src = url;
    });
    const k = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight));
    const c = document.createElement("canvas");
    c.width = Math.round(img.naturalWidth * k);
    c.height = Math.round(img.naturalHeight * k);
    const g = c.getContext("2d");
    if (!g) throw new Error("Your browser couldn't read that image.");
    g.drawImage(img, 0, 0, c.width, c.height);
    return c.toDataURL("image/jpeg", 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** When we couldn't see a listing's sizes: the shopper sends a screenshot of the size buttons,
 *  Claude reads them, the shopper checks the list, and the sizes are saved for the product so
 *  the next person who pastes the link gets them too. */
export function ScreenshotSizes({ product, onConfirmed }: { product: Product; onConfirmed: (p: Product) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [read, setRead] = useState<{ sizes: string[]; fits: string[] } | null>(null);
  const [busy, setBusy] = useState<"reading" | "saving" | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setErr(null);
    setBusy("reading");
    try {
      setRead(await api.readSizeScreenshot(product.id, await shrink(file)));
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(null);
      if (input.current) input.current.value = "";
    }
  };
  const confirm = async () => {
    if (!read?.sizes.length) return;
    setBusy("saving");
    setErr(null);
    try {
      const { product: p } = await api.confirmSizes(product.id, read);
      setRead(null);
      onConfirmed(p);
    } catch (x) {
      setErr(errorMessage(x));
    } finally {
      setBusy(null);
    }
  };
  const drop = (s: string) => read && setRead({ ...read, sizes: read.sizes.filter((x) => x !== s) });

  return (
    <div className="stack">
      <input ref={input} type="file" accept="image/*" hidden aria-hidden="true" onChange={(e) => void pick(e.target.files?.[0])} />
      {!read && (
        <>
          <p className="small muted">You can see the real sizes on {product.brand}'s page, even if we can't.</p>
          <button className="btn" type="button" disabled={busy !== null} onClick={() => input.current?.click()}>
            {busy === "reading" ? "Reading your screenshot…" : "Add a screenshot of the size buttons"}
          </button>
        </>
      )}
      {read && (
        <>
          <p className="small">Claude read these from your screenshot. Tap any that aren't there to remove them.</p>
          <div className="row" role="group" aria-label="Sizes read from your screenshot">
            {read.sizes.map((s) => (
              <button key={s} className="chip size" type="button" aria-label={`Remove ${s}`} onClick={() => drop(s)}>
                {s} <span aria-hidden="true">×</span>
              </button>
            ))}
          </div>
          {read.fits.length > 1 && <p className="small muted">Fits: {read.fits.join(", ")}</p>}
          <button className="btn primary" type="button" disabled={busy !== null || !read.sizes.length} onClick={() => void confirm()}>
            {busy === "saving" ? "Saving…" : "Use these sizes"}
          </button>
          <button className="linkish" type="button" disabled={busy !== null} onClick={() => input.current?.click()}>
            Not right? Try another screenshot
          </button>
        </>
      )}
      {err && <p className="hint warn">{err}</p>}
    </div>
  );
}
